<template>
  <div class="p-5 sparkline">
    <div class="flex items-center text-sm font-medium text-gray-800">
      {{ title }}
      <div class="px-1 ml-1 text-xs text-emerald-800 bg-emerald-200 rounded-md" v-if="change">
        {{ change }}%
      </div>
    </div>
    <Loading v-if="loading" height="8rem" :color="color" />
    <template v-else>
      <div class="flex justify-between mt-1">
        <div class="leading-none">
          <div class="text-lg" :style="{ color }" :class="{ 'opacity-0': primary.value === 'N/A' }">
            {{ primary.value }}
          </div>
          <div class="text-xs" :class="{ 'opacity-0': primary.period === 'N/A' }">
            {{ primary.period }}
          </div>
        </div>
        <div class="leading-none">
          <div class="text-gray-500" :class="{ 'opacity-0': secondary.value === 'N/A' }">
            {{ secondary.value }}
          </div>
          <div class="text-xs" :class="{ 'opacity-0': secondary.period === 'N/A' }">
            {{ secondary.period }}
          </div>
        </div>
      </div>
      <div class="border-b border-gray-300" @mouseleave="index = null">
        <apex-chart 
          type="line"
          :height="80" 
          :options="chartOptions" 
          :series="series" />
      </div>
      <div class="flex justify-between mt-1 text-xs text-gray-500">
        <div>{{ start | moment('MMM YYYY') }}</div>
        <div>{{ end | moment('MMM YYYY') }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import ApexChart from 'vue-apexcharts'
import moment from 'moment'

export default {
  name: 'Sparkline',

  components: {
    ApexChart
  },

  props: {
    title: {
      type: String,
      default: 'Untitled'
    },

    change: {
      type: Number,
      default: 0
    },

    loading: {
      type: Boolean
    },

    color: {
      type: String,
      default: '#3E83F8'
    },

    start: {
      type: String,
      default: new Date().toISOString()
    },

    end: {
      type: String,
      default: new Date().toISOString()
    },

    series: {
      type: Array,
      default: []
    },

    total: {
      type: Object,
      default: () => { return { primary: 0, secondary: 0 } }
    },

    format: {
      type: Function,
      default: (val) => { return val }
    }
  },

  computed: {
    chartOptions () {
      return {
        ...this.defaultOptions,
        ...this.options
      }
    },

    primary () {
      let series = this.series[0]
      let index = this.index
      if (!series) {
        return { 
          value: 'N/A', 
          period: 'N/A' 
        }
      } else if (index !== null && index > -1) {
        return {
          value: this.format(series.data[index]['y']),
          period: moment(series.data[index]['x']).format('MMM YYYY')
        }
      } else {
        return {
          value: this.format(this.total.primary),
          period: 'N/A'
        }
      }
    },

    secondary () {
      let series = this.series[1]
      let index = this.index
      if (!series) {
        return { 
          value: 'N/A', 
          period: 'N/A' 
        }
      } else if (index !== null && index > -1) {
        return {
          value: this.format(series.data[index]['y']),
          period: moment(series.data[index]['x']).format('MMM YYYY')
        }
      } else {
        return {
          value: this.format(this.total.secondary),
          period: 'N/A'
        }
      }
    },
  },

  data () {
    return {
      index: null,
      defaultOptions: {
        chart: {
          sparkline: {
            enabled: true
          },
          events: {
            mouseMove: (event, chartContext, config) => {
              this.index = config.dataPointIndex
            }
          }
        },
        colors: [this.color, '#718096'],
        dataLabels: {
          enabled: false
        },
        grid: {
          show: false,
          borderColor: '#cbd5e0'
        },
        stroke: {
          curve: 'straight',
          width: 2
        },
        tooltip: {
          custom: function({series, seriesIndex, dataPointIndex, w}) {
            return ''
          }
        },
        markers: {
          size: 0,
          strokeWidth: 2,
          strokeOpacity: 0,
          hover: {
            size: 3
          }
        }
      }
    }
  }
}
</script>

<style lang="postcss">
  .sparkline {
    .apexcharts-tooltip,
    .apexcharts-tooltip.active,
    .apexcharts-xaxistooltip,
    .apexcharts-xaxistooltip.active {
      @apply hidden;
    }

    .apexcharts-marker {
      @apply transition-none;
    }
  }
</style>